import { Injectable, computed, signal } from '@angular/core';

export type DocsTheme = 'dark' | 'light';

const STORAGE_KEY = 'mila_docs_theme';

/** Which side of --docs-* (styles.scss) resolves dark or light. Scoped
 *  to the docs site only (see DocsLayout's own class comment for why
 *  /docs has its own token layer instead of using --color-*): applied as
 *  `data-docs-theme` on <html> rather than <body> so it's already set
 *  before DocsLayout (or the search modal, which mounts outside
 *  .docs-page's own subtree at the app root see styles.scss:3570)
 *  renders anything.
 *
 *  Light is the default for a visitor with no stored preference, matching
 *  the Figma docs frames; a stored choice (either theme) still wins. */
@Injectable({ providedIn: 'root' })
export class DocsThemeService {
  private readonly _theme = signal<DocsTheme>(this.readStored() ?? 'light');
  readonly theme = this._theme.asReadonly();
  readonly isLight = computed(() => this._theme() === 'light');

  constructor() {
    this.apply(this._theme());
  }

  toggle(): void {
    this.set(this._theme() === 'dark' ? 'light' : 'dark');
  }

  set(theme: DocsTheme): void {
    this._theme.set(theme);
    localStorage.setItem(STORAGE_KEY, theme);
    this.apply(theme);
  }

  private apply(theme: DocsTheme): void {
    document.documentElement.setAttribute('data-docs-theme', theme);
  }

  private readStored(): DocsTheme | null {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  }
}
